/**
 * Sanitized provider-login health contract. It deliberately has no link id,
 * filesystem path, marker metadata, provider payload, account email, or token.
 */
import { z } from 'zod';

export const vendorLoginVendorSchema = z.enum(['anthropic', 'openai', 'google']);
export const vendorLoginTerminalStateSchema = z.enum(['live', 'expired', 'revoked', 'invalid']);
export const vendorLoginUnknownReasonSchema = z.enum([
  'not_checked',
  'stale',
  'provider_unsupported',
  'native_probe_unavailable',
  'external_auth',
  'runtime_identity_unavailable',
  'target_changed',
  'permission_denied',
  'network',
  'timeout',
  'protocol_error',
  'transient',
  'busy',
  /**
   * A real turn on this account's own tree observed the CLI itself refuse to
   * run for lack of a subscription login (2026-09-10 incident: the token
   * strings were blank while `account_vendor_links.status` still read
   * 'connected' from a stale 2026-08-07 snapshot). Derived ONLY from that
   * observed turn outcome — never from opening the credential file — so it
   * can go stale exactly like any other observation and is reported with its
   * own `observedAt`, not asserted as the current live state.
   */
  'login_required_observed',
  'oauth_expired_observed',
  /**
   * T-73 (2026-10-04): the official CLI's own screen, during the account's
   * catalogue probe, said "Your login expires in N days" -- Claude's
   * subscription sign-in has a FIXED refresh deadline that renewals do not
   * move (claude shows the notice in the last 3 days). For this reason only,
   * `retryAt` carries that deadline (the latest instant the sign-in can still
   * renew): the founder must sign in again before it. Never read from the
   * credential file (A3.6).
   */
  'login_expiring_observed',
]);
export type VendorLoginUnknownReason = z.infer<typeof vendorLoginUnknownReasonSchema>;

export const vendorLoginStatusSchema = z.object({
  vendor: vendorLoginVendorSchema,
  ready: z.boolean(),
  authorization: z.discriminatedUnion('state', [
    z.object({ state: z.literal('present') }).strict(),
    z.object({ state: z.literal('absent') }).strict(),
    z.object({ state: z.literal('unknown'), reason: z.literal('marker_unreadable') }).strict(),
  ]),
  liveness: z.union([
    z.object({
      state: vendorLoginTerminalStateSchema,
      observedAt: z.string().datetime(),
      validUntil: z.string().datetime(),
    }).strict(),
    z.object({
      state: z.literal('unknown'),
      reason: vendorLoginUnknownReasonSchema,
      observedAt: z.string().datetime().nullable(),
      retryAt: z.string().datetime().nullable(),
    }).strict(),
  ]),
  runtime: z.object({
    provider: z.string(),
    cli: z.string(),
    version: z.string().nullable(),
    versionSource: z.enum(['image_manifest', 'app_server', 'unavailable']),
    instanceOrdinal: z.number().int().positive().nullable(),
    directoryRelation: z.enum(['same', 'legacy_alias', 'unavailable']),
  }).strict(),
}).strict();
export type VendorLoginStatus = z.infer<typeof vendorLoginStatusSchema>;

/** Fail closed during rolling deployments and future enum expansion. */
export function decodeVendorLoginStatus(value: unknown): VendorLoginStatus | null {
  const parsed = vendorLoginStatusSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}
